import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Channel as BaseChannel } from '../types';

type Channel = BaseChannel & {
  lastMessageTimestamp?: string | null;
}

type User = {
  id: string;
  name: string;
  avatar_url: string | null;
  lastMessageTimestamp?: string | null;
};

export const useChannelsAndDirectMessages = () => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [directMessages, setDirectMessages] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      // Fetch channel memberships
      const { data: membershipData, error: membershipError } = await supabase
        .from('channel_members')
        .select('channel_id')
        .eq('user_id', user.id);
      
      if (membershipError) throw membershipError;
      
      let fetchedChannels: Channel[] = [];
      if (membershipData && membershipData.length > 0) {
        const channelIds = membershipData.map(m => m.channel_id);
        const { data: channelsData, error: channelsError } = await supabase
          .from('message_channels')
          .select('*')
          .in('id', channelIds)
          .order('name', { ascending: true });
        if (channelsError) throw channelsError;
        
        fetchedChannels = await Promise.all((channelsData || []).map(async (channel) => {
          const { data: lastMsgData } = await supabase
            .from('messages')
            .select('created_at')
            .eq('channel_id', channel.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          
          return {
            ...channel,
            lastMessageTimestamp: lastMsgData?.created_at || null,
          };
        }));
      }
      setChannels(fetchedChannels);
      
      // Fetch Direct Messages based on message history
      const { data: sentMessages, error: sentError } = await supabase
        .from('messages')
        .select('recipient_id')
        .eq('sender_id', user.id)
        .not('recipient_id', 'is', null);
        
      const { data: receivedMessages, error: receivedError } = await supabase
        .from('messages')
        .select('sender_id')
        .eq('recipient_id', user.id);
        
      if (sentError || receivedError) throw sentError || receivedError;
      
      const sentContactIds = sentMessages?.map(msg => msg.recipient_id).filter(id => id !== null) || [];
      const receivedContactIds = receivedMessages?.map(msg => msg.sender_id).filter(id => id !== null) || [];
      const uniqueContactIds = [...new Set([...sentContactIds, ...receivedContactIds])].filter(id => id !== user.id);

      let fetchedDirectMessages: User[] = [];
      if (uniqueContactIds.length > 0) {
        const { data: contactsData, error: contactsError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, avatar_url')
          .in('id', uniqueContactIds);
        
        if (contactsError) throw contactsError;
        
        fetchedDirectMessages = await Promise.all((contactsData || []).map(async (profile) => {
          const { data: lastMsgData } = await supabase
            .from('messages')
            .select('created_at')
            .or(`and(sender_id.eq.${user.id},recipient_id.eq.${profile.id}),and(sender_id.eq.${profile.id},recipient_id.eq.${user.id})`)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          return {
            id: profile.id,
            name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
            avatar_url: profile.avatar_url,
            lastMessageTimestamp: lastMsgData?.created_at || null,
          };
        }));
      }
      setDirectMessages(fetchedDirectMessages);

    } catch (error) {
      console.error('Error fetching conversations:', error);
      setChannels([]);
      setDirectMessages([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const refetchChannels = () => {
    fetchData();
  };
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  return {
    channels,
    directMessages,
    loading,
    refetchChannels
  };
};
